import type { PlayerRow } from './api.js';

/**
 * What each robot is called and what colour it is drawn in.
 *
 * Shared by the live board and the replay: two screens showing the same
 * game must not disagree about which robot is the red one.
 */

/**
 * The eight robot colours, in seat order.
 *
 * BRIGHTER THAN THE BOARDS. They used to be picked to sit with the site's
 * own paint -- `--paint-red` and the rest in `styles.css`, which are the
 * same three values -- and that is a palette for flat chrome on a plain
 * ground. A robot is a small shape on printed board art, and against a
 * busy tile a muted colour reads as part of the floor.
 *
 * Hues are unchanged and so is the order, because a player knows which
 * one they are: seat 1 is the red one before and after.
 *
 * The two ends are the awkward ones and are handled rather than lifted
 * with the rest. Seat 7 stays the darkest of the eight -- it is the
 * "black" robot -- but slate rather than near-black, which was a hole in
 * dark board art. Seat 8 goes the other way, to near-white: it is lighter
 * than the floor now instead of darker, which works because the robot art
 * is drawn with a dark outline and that is what separates it from the
 * ground.
 */
export const COLOURS = [
  '#d9503f', '#3d86ad', '#f2c94c', '#57a06a',
  '#9a70c4', '#e5842c', '#4b5058', '#d5dbcf',
];

/**
 * What to call each robot on screen: the player's own name.
 *
 * `robot1` is an internal identity -- it is how the engine, the database
 * and every authorisation check refer to a seat -- and there is no reason
 * to show it to anyone. Callers fall back to the id only when the player
 * list has not loaded or a robot has no player.
 */
export function nameMap(players: PlayerRow[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const p of players) map.set(p.robot_id, p.display_name);
  return map;
}

/**
 * Colour is keyed on the SEAT, not on array order.
 *
 * It used to be the index of whichever list happened to be available,
 * which meant two players could see the same robot in different colours,
 * and a robot could change colour when the player list arrived. The seat
 * is fixed for the whole game and identical on every client, so everyone
 * sees the same board.
 *
 * Robot ids are `robot<seat>`, so the seat is recoverable even before the
 * player list has loaded.
 */
export function colourMap(
  players: PlayerRow[], robots: { id: string }[],
): Map<string, string> {
  const map = new Map<string, string>();

  const seatOf = (robotId: string) => {
    const fromPlayers = players.find((p) => p.robot_id === robotId)?.seat;
    if (fromPlayers !== undefined) return fromPlayers;
    const digits = robotId.match(/(\d+)$/);
    return digits ? Number(digits[1]) : 1;
  };

  for (const p of players) {
    map.set(p.robot_id, p.colour ?? COLOURS[(p.seat - 1) % COLOURS.length]);
  }
  for (const r of robots) {
    if (!map.has(r.id)) map.set(r.id, COLOURS[(seatOf(r.id) - 1) % COLOURS.length]);
  }
  return map;
}
